// The pieces a settings screen is made of: grouped rows, switches, and confirmations.
//
// Kept out of ui.tsx because these are a family rather than a set of unrelated widgets — a row's
// padding, its hairline, and the way a switch sits inside it only make sense together, and the
// whole point is that every row in the screen lines up.
//
// Accessibility is the reason these exist at all rather than being written inline per row. A settings
// screen is mostly controls, so the details that get skipped when hand-rolling — a real `role="switch"`
// with `aria-checked`, a label bound to the control that toggles it, focus that is visible on a dark
// glass surface, a dialog that traps focus and restores it — are exactly the details that decide
// whether the screen can be used without a mouse.

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconChevronRight } from './Icons';

/* ── Sections and rows ─────────────────────────────────────────────────────────────────────────── */

/**
 * A titled group of rows. `title` is optional because iOS deliberately leaves the destructive group
 * unlabelled — "Danger zone" as a heading announces a category, where an unheaded pair of red rows
 * reads as two specific actions, which is what they are.
 */
export function SettingsSection({
  icon,
  title,
  footnote,
  children,
}: {
  icon?: ReactNode;
  title?: string;
  footnote?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="oset-section">
      {title ? (
        <h2 className="oset-title">
          {icon ? <span className="oset-title-icon">{icon}</span> : null}
          {title}
        </h2>
      ) : null}
      <div className="oset-group">{children}</div>
      {footnote ? <p className="oset-foot">{footnote}</p> : null}
    </section>
  );
}

interface RowContent {
  label: string;
  /** The line under the label. Explains consequence, not the control — "Blur entries", not "Toggle". */
  hint?: ReactNode;
  /** Trailing state, shown before any chevron: "Verified", "12.4 MB", "On". */
  value?: ReactNode;
  icon?: ReactNode;
  danger?: boolean;
}

/** A row that goes somewhere or does something. Renders a button so keyboard and screen readers agree. */
export function SettingsAction({
  onClick,
  disabled,
  chevron = true,
  ...content
}: RowContent & { onClick(): void; disabled?: boolean; chevron?: boolean }) {
  return (
    <button
      type="button"
      className={`oset-row is-tappable${content.danger ? ' is-danger' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      <RowBody {...content} />
      {chevron ? <IconChevronRight className="oset-chev" /> : null}
    </button>
  );
}

/** A row that leaves the app, or leaves the SPA. `external` opens in a new tab. */
export function SettingsLink({
  href,
  external,
  ...content
}: RowContent & { href: string; external?: boolean }) {
  return (
    <a
      className="oset-row is-tappable"
      href={href}
      {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
    >
      <RowBody {...content} />
      <IconChevronRight className="oset-chev" />
    </a>
  );
}

/** A row that only states something: "End-to-end encryption · On". */
export function SettingsStatic(content: RowContent) {
  return (
    <div className="oset-row">
      <RowBody {...content} />
    </div>
  );
}

/**
 * A row whose control is a switch.
 *
 * The whole row is the label, so the hint text is clickable too and the target is the full width
 * rather than a 44px pill at the end of it. `aria-describedby` puts the hint into the announcement,
 * because "Pause memory-building" alone does not say what it will stop.
 */
export function SettingsSwitch({
  checked,
  onChange,
  disabled,
  ...content
}: RowContent & { checked: boolean; onChange(next: boolean): void; disabled?: boolean }) {
  const id = useId();
  const hintId = content.hint ? `${id}-hint` : undefined;

  return (
    <div className={`oset-row${disabled ? ' is-disabled' : ''}`}>
      <label className="oset-body" htmlFor={id}>
        <span className="oset-label">{content.label}</span>
        {content.hint ? (
          <span className="oset-hint" id={hintId}>
            {content.hint}
          </span>
        ) : null}
      </label>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-describedby={hintId}
        className="oswitch"
        disabled={disabled}
        onClick={() => onChange(!checked)}
      >
        <span className="oswitch-knob" aria-hidden />
      </button>
    </div>
  );
}

function RowBody({ label, hint, value, icon, danger }: RowContent) {
  return (
    <>
      {icon ? <span className={`oset-icon${danger ? ' is-danger' : ''}`}>{icon}</span> : null}
      <span className="oset-body">
        <span className="oset-label">{label}</span>
        {hint ? <span className="oset-hint">{hint}</span> : null}
      </span>
      {value ? <span className="oset-value">{value}</span> : null}
    </>
  );
}

/* ── Confirmation ──────────────────────────────────────────────────────────────────────────────── */

export interface ConfirmSpec {
  title: string;
  body: ReactNode;
  /** Button text. Name the outcome — "Erase everything", not "OK". */
  confirmLabel: string;
  /** Require this word typed exactly. Reserved for what cannot be undone. */
  typed?: string;
  /** Ask for one value first — a new email, a name. */
  input?: { label: string; placeholder?: string; type?: string; initial?: string };
  danger?: boolean;
  onConfirm(value: string): Promise<void> | void;
}

/**
 * The dialog that replaced `window.prompt`.
 *
 * `prompt` was doing real work here — it gated account deletion behind typing DELETE — but it cannot
 * be styled, reads as a browser security warning rather than as part of the app, is suppressed
 * outright by some browsers, and gave the most consequential action in the product the least
 * considered presentation of anything on screen.
 */
export function ConfirmDialog({ spec, onClose }: { spec: ConfirmSpec | null; onClose(): void }) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const returnTo = useRef<Element | null>(null);

  useEffect(() => {
    if (!spec) return;
    setValue(spec.input?.initial ?? '');
    setError(null);

    // Remember where focus was so dismissing puts it back on the row that opened this, rather than
    // dropping the user at the top of the document.
    returnTo.current = document.activeElement;
    const field = cardRef.current?.querySelector<HTMLElement>('input');
    (field ?? cardRef.current)?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab') return;
      // Keep Tab inside the dialog: behind it sit every row of the settings screen, and tabbing into
      // them while a destructive confirmation is open is how you confirm something you cannot see.
      const focusable = cardRef.current?.querySelectorAll<HTMLElement>(
        'button:not(:disabled), input, a[href]',
      );
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      (returnTo.current as HTMLElement | null)?.focus?.();
    };
  }, [spec, onClose]);

  if (!spec) return null;

  const ready = spec.typed
    ? value === spec.typed
    : spec.input
      ? value.trim().length > 0
      : true;

  async function confirm() {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    try {
      await spec!.onConfirm(value.trim());
      onClose();
    } catch (err) {
      // Kept in the dialog rather than behind it: the action failed here, and this is where the
      // person is looking.
      setError(err instanceof Error ? err.message : 'That did not work. Try again.');
    } finally {
      setBusy(false);
    }
  }

  // Rendered into <body>, not in place.
  //
  // `.oscroll` is `position: absolute; z-index: 1`, which makes it a stacking context — so every
  // z-index used inside it, however large, is still resolved *within* that single layer. The nav pill
  // is a sibling of `.oscroll` at z-index 30, so it painted straight over this dialog's buttons: the
  // confirm and cancel for erasing every entry sat underneath the navigation bar. A portal moves the
  // dialog out to the top level, where its z-index means what it says.
  return createPortal(
    <>
      <div className="oconfirm-scrim" onClick={onClose} aria-hidden />
      <div className="oconfirm-wrap" role="presentation">
        <div
          className="oconfirm"
          ref={cardRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby="oconfirm-title"
        >
          <h2 className="oconfirm-title" id="oconfirm-title">
            {spec.title}
          </h2>
          <div className="oconfirm-body">{spec.body}</div>

          {spec.input ? (
            <label className="oconfirm-field">
              <span className="olabel">{spec.input.label}</span>
              <input
                className="oinput"
                type={spec.input.type ?? 'text'}
                value={value}
                placeholder={spec.input.placeholder}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void confirm();
                }}
              />
            </label>
          ) : null}

          {spec.typed ? (
            <label className="oconfirm-field">
              <span className="olabel">Type {spec.typed} to confirm</span>
              <input
                className="oinput mono"
                value={value}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                placeholder={spec.typed}
                onChange={(e) => setValue(e.target.value)}
              />
            </label>
          ) : null}

          {error ? <p className="onotice danger">{error}</p> : null}

          <div className="oconfirm-actions">
            <button type="button" className="oconfirm-cancel" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className={`oconfirm-go${spec.danger ? ' is-danger' : ''}`}
              onClick={confirm}
              disabled={!ready || busy}
            >
              {busy ? 'Working…' : spec.confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
